/**
 * <cart-drawer-suggestions>
 *
 * Mounts inside the cart-drawer section, between the line items and the
 * checkout footer. Two tabs:
 *   - For You  → personalised Shopify product recommendations
 *   - Saved    → Swym wishlist (logged-in only; tab hidden otherwise)
 *
 * Re-renders automatically with the cart-drawer section because it lives
 * inside the same render tree. connectedCallback re-binds on every mount.
 */
class CartDrawerSuggestions extends HTMLElement {
  constructor() {
    super();
    this.recsLoaded = false;
    this.wishlistLoaded = false;
    this.boundOnTabClick = this.onTabClick.bind(this);
    this.boundOnRowClick = this.onRowClick.bind(this);
  }

  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-cds-tab]'));
    this.tabsContainer = this.querySelector('.cds__tabs');
    this.pages = this.querySelector('[data-cds-pages]');
    this.indicator = this.querySelector('.cds__tabs-indicator');
    this.recsRow = this.querySelector('[data-cds-recs-row]');
    this.recsState = this.querySelector('[data-cds-recs-state]');
    this.wishlistRow = this.querySelector('[data-cds-wishlist-row]');
    this.wishlistState = this.querySelector('[data-cds-wishlist-state]');
    this.wishlistCount = this.querySelector('[data-cds-wishlist-count]');
    this.isLoggedIn = this.dataset.loggedIn === 'true';

    this.tabs.forEach((tab) => tab.addEventListener('click', this.boundOnTabClick));
    this.addEventListener('click', this.boundOnRowClick);

    // Collapse toggle. The cart drawer re-renders on every cart change, so the
    // collapsed preference is persisted in localStorage and re-applied on mount.
    this.collapseBtn = this.querySelector('[data-cds-collapse]');
    if (this.collapseBtn) {
      this.applyCollapsed(this.readCollapsed(), false);
      this.boundOnCollapse = () => this.applyCollapsed(!this.readCollapsed(), true);
      this.collapseBtn.addEventListener('click', this.boundOnCollapse);
    }

    // The pill indicator measures via getBoundingClientRect — that returns
    // zeros if the drawer hasn't been laid out yet (e.g. quick-shop inserts
    // the strip while the drawer is still opening). Reposition on every
    // size change of the tabs container so the indicator catches up once
    // it actually has dimensions.
    if (typeof ResizeObserver !== 'undefined' && this.tabsContainer) {
      this.tabsResizeObserver = new ResizeObserver(() => this.positionIndicator());
      this.tabsResizeObserver.observe(this.tabsContainer);
    }
    this.positionIndicator();
    requestAnimationFrame(() => this.positionIndicator());

    this.loadRecommendations();

    if (this.isLoggedIn) {
      this.preloadWishlistCount();
    }

    requestAnimationFrame(() => {
      if (this.pages) this.pages.style.removeProperty('--page-exit-enabled');
    });
  }

  disconnectedCallback() {
    if (this.tabsResizeObserver) {
      this.tabsResizeObserver.disconnect();
      this.tabsResizeObserver = null;
    }
    if (this.onPickerKeydown) {
      document.removeEventListener('keydown', this.onPickerKeydown);
      this.onPickerKeydown = null;
    }
    this.activePicker = null;
  }

  /* ---------- Collapse ---------- */

  readCollapsed() {
    // Default collapsed: the panel overlays the cart items, so it should start
    // closed and only open when the shopper taps the bar. A stored preference
    // still wins on subsequent renders.
    try {
      const v = localStorage.getItem('reasn:cds-collapsed');
      if (v === null) return true;
      return v === '1';
    } catch (e) {
      return true;
    }
  }

  applyCollapsed(collapsed, persist) {
    this.classList.toggle('cds--collapsed', collapsed);
    if (this.collapseBtn) {
      this.collapseBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      this.collapseBtn.setAttribute(
        'aria-label',
        collapsed ? 'Expand suggestions' : 'Collapse suggestions'
      );
    }
    if (persist) {
      try {
        localStorage.setItem('reasn:cds-collapsed', collapsed ? '1' : '0');
      } catch (e) {
        /* storage unavailable — state still applies for this render */
      }
      // The indicator may have been measured while hidden; refresh on expand.
      if (!collapsed) requestAnimationFrame(() => this.positionIndicator());
    }
  }

  /* ---------- Tabs ---------- */

  onTabClick(event) {
    const tab = event.currentTarget;
    const name = tab.dataset.cdsTab;
    if (!name) return;
    if (name === 'wishlist' && !this.isLoggedIn) {
      event.preventDefault();
      this.openAuthModal();
      return;
    }
    this.switchTab(name);
  }

  openAuthModal() {
    const modal = this.querySelector('[data-cds-auth-modal]') || this.authModalEl;
    if (!modal) return;
    if (modal.parentElement !== document.body) {
      this.authModalEl = modal;
      document.body.appendChild(modal);
    }
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    if (!this.boundAuthClose) {
      this.boundAuthClose = (e) => {
        if (e.target.closest('[data-cds-auth-close]')) this.closeAuthModal();
      };
      modal.addEventListener('click', this.boundAuthClose);
    }
    if (!this.boundAuthKey) {
      this.boundAuthKey = (e) => {
        if (e.key === 'Escape') this.closeAuthModal();
      };
      document.addEventListener('keydown', this.boundAuthKey);
    }
    if (window.reasnAnalytics) {
      window.reasnAnalytics.track('cart_suggestions_wishlist_auth_prompted');
    }
  }

  closeAuthModal() {
    const modal = this.querySelector('[data-cds-auth-modal]') || this.authModalEl;
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    if (this.boundAuthKey) {
      document.removeEventListener('keydown', this.boundAuthKey);
      this.boundAuthKey = null;
    }
  }

  switchTab(name) {
    const previous = this.tabs.find((t) => t.classList.contains('is-active'));
    const previousName = previous ? previous.dataset.cdsTab : '';
    this.tabs.forEach((tab) => {
      const active = tab.dataset.cdsTab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    if (this.pages) this.pages.setAttribute('data-page', name);
    this.positionIndicator();
    if (window.reasnAnalytics && previousName !== name) {
      window.reasnAnalytics.track('cart_suggestions_tab_switched', {
        from: previousName,
        to: name
      });
    }

    if (name === 'wishlist' && this.isLoggedIn && !this.wishlistLoaded) {
      this.loadWishlist();
    }
  }

  positionIndicator() {
    if (!this.indicator) return;
    const active = this.tabs.find((t) => t.classList.contains('is-active'));
    if (!active) return;
    const tabsRect = active.parentElement.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const x = activeRect.left - tabsRect.left;
    const w = activeRect.width;
    this.indicator.style.setProperty('--cds-indicator-x', `${x}px`);
    this.indicator.style.setProperty('--cds-indicator-w', `${w}px`);
  }

  /* ---------- Recommendations ---------- */

  loadRecommendations() {
    if (this.recsLoaded) return;
    const base = this.dataset.recommendationsBase;
    if (!base || base.includes('product_id=&') || base.endsWith('product_id=')) {
      // No seed product (empty cart) — keep the server-rendered fallback.
      this.recsLoaded = true;
      return;
    }
    const limit = parseInt(this.dataset.recommendationsLimit, 10) || 8;
    const cartIds = new Set(
      (this.dataset.cartProductIds || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    );

    // Show spinner only if the server didn't ship any fallback cards;
    // when there are fallback cards we just hot-swap them silently.
    const hasServerCards = this.recsRow && this.recsRow.children.length > 0;
    if (!hasServerCards) this.showRecsLoader(true);

    // Strategy: complementary first (AOV play — "frequently bought together"),
    // fall back to related to fill any remaining slots. Cart items filtered out.
    //
    // Shopify's recommendations API is async ML — the first hit for a product
    // often returns `performed=false` with zero products while the engine
    // computes. We retry with backoff until it warms up.
    this.fetchRecsWithRetry(`${base}&intent=complementary`)
      .then((complResult) => {
        const filteredCompl = this.filterCards(complResult.cards, cartIds);
        if (filteredCompl.length >= limit) return filteredCompl.slice(0, limit);

        const seenIds = new Set(filteredCompl.map((el) => el.dataset.productId).filter(Boolean));
        return this.fetchRecsWithRetry(`${base}&intent=related`).then((relatedResult) => {
          const merged = filteredCompl.slice();
          for (const card of relatedResult.cards) {
            if (merged.length >= limit) break;
            const id = card.dataset.productId;
            if (id && (cartIds.has(id) || seenIds.has(id))) continue;
            merged.push(card);
            if (id) seenIds.add(id);
          }
          return merged;
        });
      })
      .then((cards) => {
        if (cards && cards.length > 0 && this.recsRow) {
          this.recsRow.innerHTML = '';
          cards.forEach((card) => this.recsRow.appendChild(card));
        }
        // Empty result — keep server-rendered fallback (or show empty state).
      })
      .catch(() => {
        /* keep fallback row */
      })
      .finally(() => {
        this.showRecsLoader(false);
        this.recsLoaded = true;
      });
  }

  showRecsLoader(show) {
    if (!this.recsState || !this.recsRow) return;
    this.recsState.hidden = !show;
    this.recsRow.hidden = show;
  }

  /**
   * Fetch recommendations and retry while Shopify's ML reports either
   * `performed=false` (still computing) OR `performed=true` with empty
   * products on the very first attempt (fresh-seed warm-up). Returns
   * { cards, performed } so the caller can decide what to do with empty.
   */
  fetchRecsWithRetry(url, attempt = 0) {
    const maxAttempts = 4;
    const delays = [0, 600, 1200, 2000];
    return new Promise((resolve) => {
      const run = () => {
        this.fetchRecCards(url).then((result) => {
          const isWarm = result.performed && result.cards.length > 0;
          const isHardEmpty = result.performed && attempt >= 1; // performed twice with 0 cards = genuinely empty
          if (isWarm || isHardEmpty || attempt >= maxAttempts - 1) {
            resolve(result);
          } else {
            this.fetchRecsWithRetry(url, attempt + 1).then(resolve);
          }
        });
      };
      if (delays[attempt]) {
        setTimeout(run, delays[attempt]);
      } else {
        run();
      }
    });
  }

  fetchRecCards(url) {
    return fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((html) => {
        const row = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-cds-recs-row]');
        const performed = row ? row.dataset.recsPerformed === 'true' : false;
        return {
          cards: row ? Array.from(row.children) : [],
          performed,
        };
      })
      .catch(() => ({ cards: [], performed: false }));
  }

  filterCards(cards, excludedIds) {
    return cards.filter((card) => {
      const id = card.dataset.productId;
      return !id || !excludedIds.has(id);
    });
  }

  /* ---------- Wishlist (Swym) ---------- */

  whenSwymReady(fn, timeoutMs = 8000) {
    if (window._swat) return fn();
    window.SwymCallbacks = window.SwymCallbacks || [];
    let fired = false;
    const wrapped = () => {
      if (fired) return;
      fired = true;
      fn();
    };
    window.SwymCallbacks.push(wrapped);
    setTimeout(() => {
      if (fired) return;
      if (window._swat) wrapped();
      else fn(new Error('Swym did not load'));
    }, timeoutMs);
  }

  preloadWishlistCount() {
    this.whenSwymReady((err) => {
      if (err || !window._swat) return;
      window._swat.fetchLists({
        callbackFn: (lists) => this.setWishlistCount(this.totalItems(lists)),
        errorFn: () => {},
      });
    });
  }

  loadWishlist() {
    if (this.wishlistLoaded) return;
    this.setWishlistState('loading');

    this.whenSwymReady((err) => {
      if (err || !window._swat) {
        this.setWishlistState('error', "We couldn't load your saved items. Try again later.");
        return;
      }
      window._swat.fetchLists({
        callbackFn: (lists) => {
          this.wishlistLoaded = true;
          const items = this.flattenItems(lists);
          this.setWishlistCount(items.length);
          this.renderWishlistItems(items);
        },
        errorFn: () => {
          this.setWishlistState('error', "We couldn't load your saved items. Try again later.");
        },
      });
    });
  }

  flattenItems(lists) {
    if (!Array.isArray(lists)) return [];
    const out = [];
    lists.forEach((list) => {
      if (!Array.isArray(list.listcontents)) return;
      list.listcontents.forEach((item) => {
        out.push({ ...item, _listId: list.lid });
      });
    });
    return out;
  }

  totalItems(lists) {
    return this.flattenItems(lists).length;
  }

  renderWishlistItems(items) {
    if (!this.wishlistRow) return;

    if (items.length === 0) {
      this.setWishlistState('empty', 'Save items you love by tapping the heart icon — they\'ll show up here.');
      this.wishlistRow.hidden = true;
      return;
    }

    this.wishlistRow.innerHTML = items.map((item) => this.wishlistItemHTML(item)).join('');
    this.wishlistRow.hidden = false;
    if (this.wishlistState) this.wishlistState.hidden = true;
  }

  wishlistItemHTML(item) {
    const variantId = item.epi;
    const empi = item.empi;
    const url = item.du || '#';
    const handle = this.handleFromUrl(url);
    const title = this.escape(item.dt || '');
    const image = item.iu || '';
    const priceCents = typeof item.pr === 'number' ? Math.round(item.pr * 100) : null;
    const price = priceCents != null ? this.formatMoney(priceCents) : '';

    const priceMarkup = price
      ? `<div class="price">
           <div class="price__container">
             <div class="price__regular">
               <span class="visually-hidden visually-hidden--inline">Regular price</span>
               <span class="price-item price-item--regular">${price}</span>
             </div>
           </div>
         </div>`
      : '';

    return `
      <li class="cds-card" data-cds-card data-wishlist-item data-list-id="${this.escape(item._listId || '')}" data-empi="${this.escape(String(empi || ''))}" data-epi="${this.escape(String(variantId || ''))}">
        <a href="${this.escape(url)}" class="cds-card__media" tabindex="-1" aria-hidden="true">
          ${
            image
              ? `<img class="cds-card__image motion-reduce" src="${this.escape(image)}" alt="" width="120" height="120" loading="lazy">`
              : '<div class="cds-card__image cds-card__image--placeholder"></div>'
          }
          <button
            type="button"
            class="cds-card__remove"
            data-cds-remove
            aria-label="Remove ${title} from saved"
          >
            <span class="cds-card__remove-icon" aria-hidden="true">×</span>
          </button>
          <button
            type="button"
            class="cds-card__add"
            data-cds-add
            data-variant-id="${this.escape(String(variantId || ''))}"
            data-product-handle="${this.escape(handle || '')}"
            aria-label="Add ${title} to cart"
          >
            <span class="cds-card__add-icon t-text-swap" data-cds-add-label aria-hidden="true">+</span>
          </button>
        </a>
        <div class="cds-card__info">
          <h3 class="card__heading cds-card__heading">
            <a href="${this.escape(url)}" class="full-unstyled-link">${title}</a>
          </h3>
          ${priceMarkup}
        </div>
      </li>
    `;
  }

  setWishlistState(state, text) {
    if (!this.wishlistState) return;
    this.wishlistState.dataset.cdsWishlistState = state;
    this.wishlistState.hidden = false;
    if (this.wishlistRow) this.wishlistRow.hidden = state !== 'ready';
    const textEl = this.wishlistState.querySelector('.cds__wishlist-state-text');
    if (textEl && text) textEl.textContent = text;
  }

  setWishlistCount(count) {
    if (!this.wishlistCount) return;
    const wrap = this.wishlistCount.closest('[data-cds-wishlist-count-wrap]');
    const str = String(count);
    this.wishlistCount.classList.remove('is-animating');
    this.wishlistCount.replaceChildren();
    if (!count || count === 0) {
      if (wrap) wrap.hidden = true;
      return;
    }
    if (wrap) wrap.hidden = false;
    const chars = str.split('');
    chars.forEach((ch, i) => {
      const span = document.createElement('span');
      span.className = 't-digit';
      span.textContent = ch;
      if (i === chars.length - 2) span.dataset.stagger = '1';
      else if (i === chars.length - 1) span.dataset.stagger = '2';
      this.wishlistCount.appendChild(span);
    });
    void this.wishlistCount.offsetHeight; // reflow
    this.wishlistCount.classList.add('is-animating');
  }

  /* ---------- Click delegation ---------- */

  onRowClick(event) {
    const removeBtn = event.target.closest('[data-cds-remove]');
    if (removeBtn) {
      event.preventDefault();
      this.handleRemove(removeBtn);
      return;
    }
    const addBtn = event.target.closest('[data-cds-add]');
    if (addBtn) {
      event.preventDefault();
      this.handleAdd(addBtn);
      return;
    }
  }

  handleRemove(btn) {
    const card = btn.closest('[data-wishlist-item]');
    if (!card || btn.disabled) return;

    const listId = card.dataset.listId;
    const empi = parseInt(card.dataset.empi, 10);
    const epi = parseInt(card.dataset.epi, 10);
    if (!listId || !empi) return;

    btn.disabled = true;

    const removeFromDom = () => {
      // Animate out, then prune. Decrement count + handle empty state.
      card.style.transition = 'opacity 180ms ease, transform 180ms ease';
      card.style.opacity = '0';
      card.style.transform = 'scale(0.96)';
      setTimeout(() => {
        const parentRow = card.parentElement;
        card.remove();
        const remaining = parentRow ? parentRow.querySelectorAll('[data-wishlist-item]').length : 0;
        this.setWishlistCount(remaining);
        if (remaining === 0) {
          this.setWishlistState('empty', 'Save items you love by tapping the heart icon — they\'ll show up here.');
          if (this.wishlistRow) this.wishlistRow.hidden = true;
        }
      }, 180);
    };

    this.whenSwymReady((err) => {
      if (err || !window._swat) {
        // Swym unavailable — bail without optimistic removal so the user
        // doesn't think a delete went through that didn't.
        btn.disabled = false;
        return;
      }
      const payload = { epi: epi || null, empi: empi };
      window._swat.deleteFromList(
        listId,
        payload,
        () => removeFromDom(),
        () => {
          btn.disabled = false;
        }
      );
    });
  }

  handleAdd(btn) {
    const variantId = parseInt(btn.dataset.variantId, 10);
    const handle = btn.dataset.productHandle;
    if (btn.disabled) return;
    if (!variantId && !handle) return;

    btn.disabled = true;

    // No handle (legacy) → fall back to direct variant add.
    if (!handle) {
      this.addToCart(variantId, btn).finally(() => { btn.disabled = false; });
      return;
    }

    fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((product) => {
        const availableVariants = product.variants.filter((v) => v.available);
        const hasOptions = (product.options || []).filter((o) => {
          const name = typeof o === 'string' ? o : o.name;
          return name && name.toLowerCase() !== 'title';
        }).length > 0;

        // Single-variant or no real options → add directly.
        if (!hasOptions || product.variants.length === 1) {
          const target = availableVariants[0] || product.variants[0];
          return this.addToCart(target.id, btn).finally(() => { btn.disabled = false; });
        }

        btn.disabled = false;
        this.openVariantPicker(product, variantId);
      })
      .catch(() => {
        // Network/parse failure — best-effort direct add if we have a variant.
        if (variantId) {
          this.addToCart(variantId, btn).finally(() => { btn.disabled = false; });
        } else {
          btn.disabled = false;
        }
      });
  }

  addToCart(variantId, btn) {
    const labelEl = btn ? btn.querySelector('[data-cds-add-label]') : null;
    const card = btn ? btn.closest('[data-cds-card]') : null;
    const source = this.pages ? this.pages.getAttribute('data-page') : '';
    if (window.reasnAnalytics) {
      window.reasnAnalytics.track('cart_suggestion_added_to_cart', {
        variant_id: String(variantId || ''),
        product_id: card ? card.getAttribute('data-empi') || '' : '',
        source: source
      });
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(() => this.refreshCartDrawer())
      .then(() => {
        if (labelEl) this.swapText(labelEl, '✓');
        if (btn) btn.classList.add('is-success');
        setTimeout(() => {
          if (labelEl) this.swapText(labelEl, '+');
          if (btn) btn.classList.remove('is-success');
        }, 1500);
      })
      .catch((err) => {
        if (labelEl) this.swapText(labelEl, '!');
        setTimeout(() => { if (labelEl) this.swapText(labelEl, '+'); }, 1500);
        throw err;
      });
  }

  /* ---------- Variant picker ---------- */

  openVariantPicker(product, preferredVariantId) {
    this.closeVariantPicker();

    const drawer = document.querySelector('#CartDrawer');
    if (!drawer) return;

    // Default selection: previously saved variant if still available, else
    // the first available variant.
    const preferred =
      (preferredVariantId && product.variants.find((v) => v.id === preferredVariantId && v.available)) ||
      product.variants.find((v) => v.available) ||
      product.variants[0];

    const optionNames = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));
    const selected = optionNames.map((_, i) => this.variantOptionValue(preferred, i));

    const picker = document.createElement('div');
    picker.className = 'cds-picker';
    picker.setAttribute('role', 'dialog');
    picker.setAttribute('aria-modal', 'true');
    picker.setAttribute('aria-label', `Choose options for ${product.title}`);
    picker.innerHTML = this.variantPickerHTML(product, selected);

    drawer.appendChild(picker);
    this.activePicker = { el: picker, product, selected, drawer };

    requestAnimationFrame(() => picker.classList.add('is-open'));

    picker.addEventListener('click', this.onPickerClick.bind(this));
    this.onPickerKeydown = (e) => { if (e.key === 'Escape') this.closeVariantPicker(); };
    document.addEventListener('keydown', this.onPickerKeydown);

    this.updatePickerState();
  }

  variantPickerHTML(product, selected) {
    const image = product.featured_image || (product.images && product.images[0]) || '';
    const optionNames = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));

    const optionsHTML = optionNames.map((name, i) => {
      const values = Array.from(new Set(product.variants.map((v) => this.variantOptionValue(v, i)).filter(Boolean)));
      // Skip options with a single value — there's no real choice to make,
      // and the preferred variant has already locked it in.
      if (values.length <= 1) return '';
      const valuesHTML = values.map((val) => {
        return `<button type="button" class="cds-picker__value" data-cds-picker-value data-option-index="${i}" data-value="${this.escape(val)}">${this.escape(val)}</button>`;
      }).join('');
      return `
        <div class="cds-picker__option" data-option-row="${i}">
          <div class="cds-picker__option-label">
            <span>${this.escape(name)}</span>
            <span class="cds-picker__option-selected" data-option-selected="${i}">${this.escape(selected[i] || '')}</span>
          </div>
          <div class="cds-picker__values">${valuesHTML}</div>
        </div>
      `;
    }).join('');

    return `
      <div class="cds-picker__backdrop" data-cds-picker-close></div>
      <div class="cds-picker__sheet" role="document">
        <div class="cds-picker__handle" aria-hidden="true"></div>
        <button type="button" class="cds-picker__close" data-cds-picker-close aria-label="Close">×</button>
        <div class="cds-picker__head">
          ${image ? `<img class="cds-picker__image" src="${this.escape(image)}" alt="" width="56" height="56">` : ''}
          <div>
            <h3 class="cds-picker__title">${this.escape(product.title)}</h3>
            <div class="cds-picker__price" data-cds-picker-price>${this.formatMoney(product.price)}</div>
          </div>
        </div>
        ${optionsHTML}
        <button type="button" class="cds-picker__cta" data-cds-picker-add disabled>Add to cart</button>
        <div class="cds-picker__error" data-cds-picker-error aria-live="polite"></div>
      </div>
    `;
  }

  onPickerClick(event) {
    if (event.target.closest('[data-cds-picker-close]')) {
      event.preventDefault();
      this.closeVariantPicker();
      return;
    }
    const valBtn = event.target.closest('[data-cds-picker-value]');
    if (valBtn) {
      const idx = parseInt(valBtn.dataset.optionIndex, 10);
      const val = valBtn.dataset.value;
      if (!this.activePicker || valBtn.classList.contains('is-unavailable-locked')) return;
      this.activePicker.selected[idx] = val;
      this.updatePickerState();
      return;
    }
    const addBtn = event.target.closest('[data-cds-picker-add]');
    if (addBtn) {
      event.preventDefault();
      this.commitVariantPicker(addBtn);
    }
  }

  updatePickerState() {
    if (!this.activePicker) return;
    const { el, product, selected } = this.activePicker;

    // Mark selected pills + flag combos that have no available variant.
    const optionNames = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));
    optionNames.forEach((_, i) => {
      const labelEl = el.querySelector(`[data-option-selected="${i}"]`);
      if (labelEl) labelEl.textContent = selected[i] || '';

      el.querySelectorAll(`[data-cds-picker-value][data-option-index="${i}"]`).forEach((btn) => {
        const value = btn.dataset.value;
        const isSelected = selected[i] === value;
        btn.classList.toggle('is-selected', isSelected);

        // Available if any variant matches this value AND the other currently-selected options.
        const matchExists = product.variants.some((v) => {
          if (!v.available) return false;
          if (this.variantOptionValue(v, i) !== value) return false;
          for (let j = 0; j < optionNames.length; j++) {
            if (j === i) continue;
            if (selected[j] && this.variantOptionValue(v, j) !== selected[j]) return false;
          }
          return true;
        });
        btn.classList.toggle('is-unavailable', !matchExists);
      });
    });

    // Resolve current selection → variant.
    const variant = product.variants.find((v) =>
      optionNames.every((_, i) => !selected[i] || this.variantOptionValue(v, i) === selected[i])
    );
    const allChosen = selected.every(Boolean);
    const cta = el.querySelector('[data-cds-picker-add]');
    const priceEl = el.querySelector('[data-cds-picker-price]');
    const errEl = el.querySelector('[data-cds-picker-error]');

    if (variant && priceEl) priceEl.textContent = this.formatMoney(variant.price);
    if (errEl) errEl.textContent = '';

    if (cta) {
      if (!allChosen) {
        cta.disabled = true;
        cta.textContent = 'Select options';
      } else if (!variant || !variant.available) {
        cta.disabled = true;
        cta.textContent = 'Sold out';
      } else {
        cta.disabled = false;
        cta.dataset.variantId = String(variant.id);
        cta.textContent = 'Add to cart';
      }
    }
  }

  commitVariantPicker(cta) {
    if (!this.activePicker || cta.disabled) return;
    const variantId = parseInt(cta.dataset.variantId, 10);
    if (!variantId) return;

    cta.disabled = true;
    const errEl = this.activePicker.el.querySelector('[data-cds-picker-error]');
    if (errEl) errEl.textContent = '';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((r) => (r.ok ? r.json() : r.json().then((j) => Promise.reject(j))))
      .then(() => {
        cta.classList.add('is-success');
        cta.textContent = 'Added ✓';
        setTimeout(() => {
          this.closeVariantPicker();
          this.refreshCartDrawer();
        }, 600);
      })
      .catch((err) => {
        cta.disabled = false;
        if (errEl) errEl.textContent = (err && err.description) || 'Could not add to cart. Please try again.';
      });
  }

  closeVariantPicker() {
    if (this.onPickerKeydown) {
      document.removeEventListener('keydown', this.onPickerKeydown);
      this.onPickerKeydown = null;
    }
    if (!this.activePicker) return;
    const { el } = this.activePicker;
    el.classList.remove('is-open');
    setTimeout(() => el.remove(), 320);
    this.activePicker = null;
  }

  // Read an option value from a variant by index. Prefers the `options[]`
  // array (supports >3 options on the new Shopify variants model); falls
  // back to legacy option1/option2/option3 fields.
  variantOptionValue(variant, index) {
    if (!variant) return '';
    if (Array.isArray(variant.options) && variant.options[index] != null) {
      return variant.options[index];
    }
    return variant[`option${index + 1}`] || '';
  }

  handleFromUrl(url) {
    if (!url) return '';
    const m = String(url).match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  /* ---------- Helpers ---------- */

  refreshCartDrawer() {
    return fetch('/?sections=cart-drawer,cart-icon-bubble')
      .then((r) => r.json())
      .then((sections) => {
        const parser = new DOMParser();
        const newDrawer = parser
          .parseFromString(sections['cart-drawer'], 'text/html')
          .querySelector('.drawer__inner');
        const currentDrawer = document.querySelector('#CartDrawer .drawer__inner');
        if (newDrawer && currentDrawer) {
          currentDrawer.innerHTML = newDrawer.innerHTML;
        }

        const bubbleEl = document.getElementById('cart-icon-bubble');
        if (sections['cart-icon-bubble'] && bubbleEl) {
          const newBubble = parser
            .parseFromString(sections['cart-icon-bubble'], 'text/html')
            .querySelector('.shopify-section');
          if (newBubble) bubbleEl.innerHTML = newBubble.innerHTML;
        }

        if (window.cartDrawerShipping?.handleCartUpdate) {
          window.cartDrawerShipping.handleCartUpdate();
        }
        document.dispatchEvent(new CustomEvent('cart:updated'));
      });
  }

  swapText(el, next) {
    const dur = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--text-swap-dur')) || 200;
    el.classList.add('is-exit');
    setTimeout(() => {
      el.textContent = next;
      el.classList.remove('is-exit');
      el.classList.add('is-enter-start');
      void el.offsetHeight;
      el.classList.remove('is-enter-start');
    }, dur);
  }

  escape(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    const amount = (cents / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || 'GBP';
    const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '';
    return `${symbol}${amount}`;
  }
}

if (!customElements.get('cart-drawer-suggestions')) {
  customElements.define('cart-drawer-suggestions', CartDrawerSuggestions);
}

// Reposition tab indicator when window resizes (drawer width can change)
window.addEventListener('resize', () => {
  document.querySelectorAll('cart-drawer-suggestions').forEach((el) => {
    if (typeof el.positionIndicator === 'function') el.positionIndicator();
  });
});
